import { Reducer } from "redux";

import {
    COUNSELLINGS_HOME_FETCH_FAILURE,
    COUNSELLINGS_HOME_FETCH_REQUEST,
    COUNSELLINGS_HOME_FETCH_SUCCESS,
    CounsellingsHomeFetchFailureAction,
    CounsellingsHomeFetchRequestAction,
    CounsellingsHomeFetchSuccessAction,
} from "@/mk2/apps/counsellings/containers/CounsellingsHome/CounsellingsHome.actions";
import { FORUM_READ_FEED_TRIGGER, ForumReadFeedTriggerAction } from "@/mk2/apps/forum/actions";
import { tupdate } from "@/mk2/helpers/types";
import infiniteList, {
    State as InfiniteListState,
    initialState as initialInfiniteListState,
} from "@/mk2/reducers/infiniteList";

const counsellingsHomeInfiniteListReducer = infiniteList<
    CounsellingsHomeFetchRequestAction,
    CounsellingsHomeFetchSuccessAction,
    CounsellingsHomeFetchFailureAction
>("COUNSELLINGS_HOME_FETCH", (a) => a.response.result.posts);

export interface TopicStats {
    [topicId: number]: number;
}

export interface CounsellingsHomePostsState extends InfiniteListState {
    resultsCount: number;
}

export const initialCounsellingsHomePostsState: CounsellingsHomePostsState = {
    ...initialInfiniteListState,
    resultsCount: 0,
};

type CounsellingsHomePostsReducerAction =
    | CounsellingsHomeFetchRequestAction
    | CounsellingsHomeFetchSuccessAction
    | CounsellingsHomeFetchFailureAction;

const counsellingsHomePostsReducer: Reducer<
    CounsellingsHomePostsState,
    CounsellingsHomePostsReducerAction
> = (state = initialCounsellingsHomePostsState, action) => {
    switch (action.type) {
        case COUNSELLINGS_HOME_FETCH_REQUEST:
        case COUNSELLINGS_HOME_FETCH_FAILURE:
            return {
                ...state,
                ...counsellingsHomeInfiniteListReducer(state, action),
            };

        case COUNSELLINGS_HOME_FETCH_SUCCESS:
            return {
                ...state,
                ...counsellingsHomeInfiniteListReducer(state, action),
                resultsCount: action.resultsCount,
            };

        default:
            return state;
    }
};

interface ByQueryState {
    [query: string]: CounsellingsHomePostsState;
}

export interface CounsellingsHomePageState {
    counsellingIds: number[];
    followingTopicIds: number[];
    lastVisitCounts: TopicStats;
    search: ByQueryState;
    topicIds: number[];
    topicStats: TopicStats;
    totalAnswers: number;
}

export const initialCounsellingsHomePageState: CounsellingsHomePageState = {
    counsellingIds: [],
    followingTopicIds: [],
    lastVisitCounts: {},
    search: {},
    topicIds: [],
    topicStats: {},
    totalAnswers: 0,
};

type PageReducerAction =
    | CounsellingsHomeFetchRequestAction
    | CounsellingsHomeFetchSuccessAction
    | CounsellingsHomeFetchFailureAction
    | ForumReadFeedTriggerAction;

const counsellingsHomePageReducer: Reducer<CounsellingsHomePageState, PageReducerAction> = (
    state = initialCounsellingsHomePageState,
    action,
) => {
    switch (action.type) {
        case COUNSELLINGS_HOME_FETCH_REQUEST:
        case COUNSELLINGS_HOME_FETCH_FAILURE: {
            const key = action.query;
            return tupdate(state, {
                search: tupdate(state.search, {
                    [key]: counsellingsHomePostsReducer(state.search[key], action),
                }),
            });
        }

        case COUNSELLINGS_HOME_FETCH_SUCCESS: {
            const key = action.query;
            return tupdate(state, {
                counsellingIds: action.response.result.counsellings,
                followingTopicIds: action.response.result.followingTopics,
                lastVisitCounts: action.lastVisitCounts,
                search: tupdate(state.search, {
                    [key]: counsellingsHomePostsReducer(state.search[key], action),
                }),
                topicIds: action.response.result.topics,
                topicStats: action.topicStats,
                totalAnswers: action.totalAnswers,
            });
        }

        case FORUM_READ_FEED_TRIGGER: {
            if (!action.feed.startsWith("subscribedCounsellings")) {
                return state;
            }

            const topicId = parseInt(action.feed.split("_")[1], 10);
            return {
                ...state,
                lastVisitCounts: {
                    ...state.lastVisitCounts,
                    [topicId]: 0,
                },
            };
        }
        default:
            return state;
    }
};

export default counsellingsHomePageReducer;
